import type { CampaignDisplayStatus, CampaignOccasion } from '@repo/shared/marketing';
import type { GetMarketingCampaignsGroup, GetMarketingCampaignsParams, GetMarketingCampaignsSort } from '@/api/generated/schemas';

export type CampaignSort = GetMarketingCampaignsSort;

/** The list's tabs, in the order they show. Running = live, scheduled and paused. */
export const CAMPAIGN_LIST_TABS = ['running', 'drafts', 'ended', 'archived'] as const;
export type CampaignListTab = (typeof CAMPAIGN_LIST_TABS)[number];
export const DEFAULT_CAMPAIGN_LIST_TAB: CampaignListTab = 'running';

/** The API's `group` for a tab (the URL says "drafts", the API "draft"). */
const TAB_GROUP: Record<CampaignListTab, GetMarketingCampaignsGroup> = {
  running: 'running',
  drafts: 'draft',
  ended: 'ended',
  archived: 'archived',
};

export function campaignListGroup(tab: CampaignListTab): GetMarketingCampaignsGroup {
  return TAB_GROUP[tab];
}

/** `?tab=` from the URL; anything unknown (or missing) is the Running tab. */
export function parseCampaignListTab(value: unknown): CampaignListTab {
  return CAMPAIGN_LIST_TABS.find((tab) => tab === value) ?? DEFAULT_CAMPAIGN_LIST_TAB;
}

/** The tab a campaign is listed under (same split as the API's `group`: Running = live, scheduled, paused). */
export function campaignListTabOf(displayStatus: CampaignDisplayStatus): CampaignListTab {
  if (displayStatus === 'DRAFT') return 'drafts';
  if (displayStatus === 'ENDED') return 'ended';
  if (displayStatus === 'ARCHIVED') return 'archived';
  return 'running';
}

/**
 * The list URL's search that opens on a campaign's own tab ("‹ Campaigns" from its hub), so the campaign is in the
 * list it lands on. Running omits the key, like the route does.
 */
export function campaignListSearchFor(displayStatus: CampaignDisplayStatus | undefined): { tab?: CampaignListTab } {
  const tab = displayStatus ? campaignListTabOf(displayStatus) : DEFAULT_CAMPAIGN_LIST_TAB;
  return tab === DEFAULT_CAMPAIGN_LIST_TAB ? {} : { tab };
}

/** Search box + the Filters sheet (occasion, sort). The tab lives in the URL, not here. */
export interface CampaignFiltersValue {
  q: string;
  occasion: CampaignOccasion | '';
  sort: CampaignSort;
}

export const DEFAULT_CAMPAIGN_FILTERS: CampaignFiltersValue = { q: '', occasion: '', sort: 'newest' };

const PAGE_SIZE = '25';

/**
 * The Campaigns list request for a tab and the filters (search already debounced). One builder for the page and its
 * prefetcher, so the list a hover warms is the one the page opens on. Sends `group`, never `status` (the API lets
 * `status` win when both are sent).
 */
export function campaignsListParams(
  tab: CampaignListTab,
  filters: Omit<CampaignFiltersValue, 'q'>,
  search: string
): GetMarketingCampaignsParams {
  return {
    limit: PAGE_SIZE,
    group: campaignListGroup(tab),
    ...(search ? { q: search } : {}),
    ...(filters.occasion ? { occasion: filters.occasion } : {}),
    sort: filters.sort,
  };
}

/** All-time funnel numbers for the summary strip + metric columns (independent of the list filters). */
export const CAMPAIGNS_ANALYTICS_PARAMS = { range: 'all' } as const;
